import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { humanError } from '../lib/errors'
import {
  itemStatusLabel,
  visibilityBadgeClass,
  visibilityHint,
  visibilityLabel,
  type Visibility,
} from '../lib/labels'

type Wishlist = {
  id: number
  title: string
  description: string
  visibility: Visibility
  ownerName: string | null
}

type Item = {
  id: number
  title: string
  url: string
  price: number | null
  note: string
  priority: number
  status: 'available' | 'reserved'
  reservedBy: number | null
  reservedByName: string | null
}

type Pledge = {
  id: number
  userId: number
  userName?: string
  amount: number
  note: string
}

type ShareInfo = {
  tokens: Array<{ id: number; token: string; url: string }>
  members: Array<{ id: number; userId: number; email: string; name: string }>
}

export function WishlistPage() {
  const { id } = useParams()
  const wishlistId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [wishlist, setWishlist] = useState<Wishlist | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const [shares, setShares] = useState<ShareInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedItem, setSelectedItem] = useState<number | null>(null)
  const [pledges, setPledges] = useState<Pledge[]>([])
  const [pledgeTotal, setPledgeTotal] = useState(0)
  const [pledgesHidden, setPledgesHidden] = useState(false)
  const [memberEmail, setMemberEmail] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)

  async function reload() {
    setError('')
    setLoading(true)
    try {
      const w = await api<{ wishlist: Wishlist; isOwner: boolean }>(`/api/wishlists/${wishlistId}`)
      setWishlist(w.wishlist)
      setIsOwner(w.isOwner)
      const i = await api<{ items: Item[] }>(`/api/items/by-wishlist/${wishlistId}`)
      setItems(i.items)
      if (w.isOwner) {
        const s = await api<ShareInfo>(`/api/shares/wishlist/${wishlistId}`)
        setShares(s)
      }
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось загрузить')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [wishlistId])

  function itemPayload(form: FormData) {
    return {
      title: String(form.get('title')),
      url: String(form.get('url') || ''),
      price: form.get('price') ? Number(form.get('price')) : null,
      note: String(form.get('note') || ''),
      priority: Number(form.get('priority') || 0),
    }
  }

  async function addItem(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    try {
      await api(`/api/items/by-wishlist/${wishlistId}`, {
        method: 'POST',
        body: JSON.stringify(itemPayload(form)),
      })
      e.currentTarget.reset()
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось добавить')
    }
  }

  async function saveItemEdit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!editingId) return
    const form = new FormData(e.currentTarget)
    try {
      await api(`/api/items/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify(itemPayload(form)),
      })
      setEditingId(null)
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось сохранить')
    }
  }

  async function deleteItem(itemId: number) {
    try {
      await api(`/api/items/${itemId}`, { method: 'DELETE' })
      if (editingId === itemId) setEditingId(null)
      if (selectedItem === itemId) setSelectedItem(null)
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось удалить')
    }
  }

  async function deleteWishlist() {
    if (!window.confirm('Удалить список целиком?')) return
    try {
      await api(`/api/wishlists/${wishlistId}`, { method: 'DELETE' })
      navigate('/app')
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось удалить список')
    }
  }

  async function createToken() {
    try {
      await api(`/api/shares/wishlist/${wishlistId}/token`, { method: 'POST' })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось создать ссылку')
    }
  }

  async function revokeToken(tokenId: number) {
    try {
      await api(`/api/shares/wishlist/${wishlistId}/tokens/${tokenId}`, { method: 'DELETE' })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось отозвать ссылку')
    }
  }

  async function addMember(e: FormEvent) {
    e.preventDefault()
    try {
      await api(`/api/shares/wishlist/${wishlistId}/members`, {
        method: 'POST',
        body: JSON.stringify({ email: memberEmail }),
      })
      setMemberEmail('')
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось выдать доступ')
    }
  }

  async function removeMember(userId: number) {
    try {
      await api(`/api/shares/wishlist/${wishlistId}/members/${userId}`, { method: 'DELETE' })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось убрать доступ')
    }
  }

  async function toggleReserve(item: Item) {
    const asOwnerUnreserve = isOwner && item.status === 'reserved'
    const asSelfUnreserve = item.status === 'reserved' && item.reservedBy === user?.id
    if (
      !asOwnerUnreserve &&
      !asSelfUnreserve &&
      item.status === 'reserved' &&
      item.reservedBy !== user?.id
    ) {
      setError(humanError('already_reserved'))
      return
    }
    const path =
      asOwnerUnreserve || asSelfUnreserve
        ? `/api/items/${item.id}/unreserve`
        : `/api/items/${item.id}/reserve`
    try {
      await api(path, { method: 'POST' })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Ошибка брони')
    }
  }

  async function openPledges(itemId: number) {
    setSelectedItem(itemId)
    try {
      const data = await api<{ pledges: Pledge[]; total: number; hidden?: boolean }>(
        `/api/pledges/by-item/${itemId}`,
      )
      setPledgesHidden(Boolean(data.hidden))
      setPledges(data.pledges)
      setPledgeTotal(data.total)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось загрузить взносы')
    }
  }

  async function savePledge(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!selectedItem) return
    const form = new FormData(e.currentTarget)
    try {
      await api(`/api/pledges/by-item/${selectedItem}`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: Number(form.get('amount')),
          note: String(form.get('note') || ''),
        }),
      })
      await openPledges(selectedItem)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось сохранить взнос')
    }
  }

  async function updateVisibility(visibility: string) {
    try {
      await api(`/api/wishlists/${wishlistId}`, {
        method: 'PATCH',
        body: JSON.stringify({ visibility }),
      })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось обновить доступ')
    }
  }

  if (loading && !wishlist) {
    return (
      <div className="app-shell">
        <div className="loading-block" role="status">
          <span className="spinner" aria-hidden="true" />
          Загрузка списка…
        </div>
      </div>
    )
  }

  if (!wishlist) {
    return (
      <div className="app-shell">
        <div className="alert alert-error" role="alert">
          {error || 'Список не найден'}
        </div>
        <Link className="back-link" to="/app">
          ← К спискам
        </Link>
      </div>
    )
  }

  const roleLabel = isOwner ? 'ваш список' : `гость · ${wishlist.ownerName ?? 'коллега'}`

  return (
    <div className="app-shell">
      <header className="topbar page-header">
        <div className="page-header-main">
          <Link className="back-link" to="/app">
            ← Назад
          </Link>
          <h1 className="section-title">{wishlist.title}</h1>
          <div className="row">
            <span className={visibilityBadgeClass(wishlist.visibility)}>
              {visibilityLabel(wishlist.visibility)}
            </span>
            <span className="badge">{roleLabel}</span>
          </div>
        </div>
        <div className="brand-mark" aria-hidden="true">
          W<span>ish</span>
        </div>
      </header>

      {error && (
        <div className="alert alert-error" role="alert" style={{ marginBottom: '0.75rem' }}>
          {error}
        </div>
      )}

      <div className="grid-2">
        <section className="panel stack">
          <h2 className="section-title">Айтемы</h2>
          <div className="list">
            {items.length === 0 && (
              <div className="empty-state">
                <strong>Пока нет позиций</strong>
                {isOwner ? 'Добавьте первый айтем в форме справа.' : 'Владелец ещё ничего не добавил.'}
              </div>
            )}
            {items.map((item) => (
              <article key={item.id} className="item-card">
                {isOwner && editingId === item.id ? (
                  <form className="stack" onSubmit={(e) => void saveItemEdit(e)}>
                    <label>
                      Название
                      <input name="title" defaultValue={item.title} required />
                    </label>
                    <label>
                      Ссылка
                      <input name="url" defaultValue={item.url} placeholder="https://" />
                    </label>
                    <label>
                      Цена
                      <input
                        name="price"
                        type="number"
                        min={0}
                        step="0.01"
                        defaultValue={item.price ?? undefined}
                      />
                    </label>
                    <label>
                      Приоритет
                      <select name="priority" defaultValue={item.priority}>
                        {[0, 1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Заметка
                      <textarea name="note" defaultValue={item.note} />
                    </label>
                    <div className="item-actions">
                      <button className="btn btn-primary" type="submit">
                        Сохранить
                      </button>
                      <button className="btn btn-ghost" type="button" onClick={() => setEditingId(null)}>
                        Отмена
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="item-card-head">
                      <strong className="item-card-title">{item.title}</strong>
                      {item.url && (
                        <a className="item-card-link" href={item.url} target="_blank" rel="noreferrer">
                          {item.url.replace(/^https?:\/\//, '').slice(0, 48)}
                          {item.url.length > 56 ? '…' : ''}
                        </a>
                      )}
                      {item.note && <p className="item-card-note">{item.note}</p>}
                      <div className="item-card-meta">
                        {item.price != null && <span className="price">{item.price} ₽</span>}
                        <span className={`badge ${item.status === 'reserved' ? 'warn' : 'accent'}`}>
                          {itemStatusLabel(item.status, {
                            isOwner,
                            reservedByName: item.reservedByName,
                          })}
                        </span>
                        <span className="badge">приоритет {item.priority}</span>
                      </div>
                    </div>
                    <div className="item-actions">
                      {!isOwner &&
                        (item.status !== 'reserved' || item.reservedBy === user?.id) && (
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={() => void toggleReserve(item)}
                          >
                            {item.status === 'reserved' && item.reservedBy === user?.id
                              ? 'Снять резерв'
                              : 'Беру в подарок'}
                          </button>
                        )}
                      {isOwner && item.status === 'reserved' && (
                        <button className="btn btn-ghost" type="button" onClick={() => void toggleReserve(item)}>
                          Снять бронь
                        </button>
                      )}
                      {isOwner && (
                        <>
                          <button className="btn" type="button" onClick={() => setEditingId(item.id)}>
                            Изменить
                          </button>
                          <button className="btn btn-ghost" type="button" onClick={() => void deleteItem(item.id)}>
                            Удалить
                          </button>
                        </>
                      )}
                      {!isOwner && (
                        <button className="btn" type="button" onClick={() => void openPledges(item.id)}>
                          Взносы
                        </button>
                      )}
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>

          {selectedItem && !isOwner && (
            <div className="panel stack subpanel">
              <h3 className="section-title">Взносы · итого {pledgeTotal} ₽</h3>
              {pledgesHidden ? (
                <p className="muted">Детали взносов скрыты для владельца списка.</p>
              ) : (
                <>
                  {pledges.length === 0 ? (
                    <p className="muted">Пока никто не скинулся — будьте первым.</p>
                  ) : (
                    <div className="list">
                      {pledges.map((p) => (
                        <div key={p.id} className="list-item">
                          <div>
                            <strong>{p.userName || `user#${p.userId}`}</strong>
                            <div className="muted">{p.note || '—'}</div>
                          </div>
                          <span className="price">{p.amount} ₽</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <form className="stack" onSubmit={(e) => void savePledge(e)}>
                    <label>
                      Сколько готов скинуть
                      <input name="amount" type="number" min={0} step="0.01" required />
                    </label>
                    <label>
                      Комментарий
                      <input name="note" placeholder="могу до пятницы" />
                    </label>
                    <button className="btn btn-primary" type="submit">
                      Сохранить взнос
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </section>

        <aside className="stack">
          {isOwner && (
            <section className="panel stack">
              <h2 className="section-title">Добавить</h2>
              <form className="stack" onSubmit={(e) => void addItem(e)}>
                <label>
                  Название
                  <input name="title" required placeholder="Kindle / наушники" />
                </label>
                <label>
                  Ссылка
                  <input name="url" placeholder="https://" />
                </label>
                <label>
                  Цена
                  <input name="price" type="number" min={0} step="0.01" />
                </label>
                <label>
                  Приоритет
                  <select name="priority" defaultValue={3}>
                    {[0, 1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Заметка
                  <textarea name="note" placeholder="размер, цвет…" />
                </label>
                <button className="btn btn-primary" type="submit">
                  Добавить айтем
                </button>
              </form>
            </section>
          )}

          {isOwner && (
            <section className="panel stack">
              <h2 className="section-title">Доступ</h2>
              <label>
                Видимость
                <select
                  value={wishlist.visibility}
                  onChange={(e) => void updateVisibility(e.target.value)}
                >
                  <option value="private">{visibilityHint('private')}</option>
                  <option value="shared">{visibilityHint('shared')}</option>
                  <option value="company">{visibilityHint('company')}</option>
                </select>
              </label>
              <button className="btn" type="button" onClick={() => void createToken()}>
                Создать share-ссылку
              </button>
              {shares?.tokens.map((t) => (
                <div key={t.id} className="list-item">
                  <code className="share-url">{t.url}</code>
                  <button className="btn btn-ghost" type="button" onClick={() => void revokeToken(t.id)}>
                    Отозвать
                  </button>
                </div>
              ))}
              <form className="stack" onSubmit={(e) => void addMember(e)}>
                <label>
                  Добавить участника по email
                  <input
                    value={memberEmail}
                    onChange={(e) => setMemberEmail(e.target.value)}
                    type="email"
                    required
                    placeholder="colleague@company.ru"
                  />
                </label>
                <button className="btn btn-primary" type="submit">
                  Выдать доступ
                </button>
              </form>
              {shares?.members.map((m) => (
                <div key={m.id} className="list-item">
                  <div>
                    <strong>{m.name}</strong>
                    <div className="muted">{m.email}</div>
                  </div>
                  <button className="btn btn-ghost" type="button" onClick={() => void removeMember(m.userId)}>
                    Убрать
                  </button>
                </div>
              ))}
              <button className="btn btn-danger" type="button" onClick={() => void deleteWishlist()}>
                Удалить список
              </button>
            </section>
          )}
        </aside>
      </div>
    </div>
  )
}
